import React from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'

const StyledInput = styled.input<{ $variant?: 'default' | 'borderless' }>`
  ${({ theme, $variant }) =>
    $variant === 'borderless'
      ? `
    background-color: ${theme.colors.surfaceHover};
    border: 2px solid transparent;
    border-radius: ${theme.borderRadius.sm};
    
    &:focus {
      outline: none;
      border-bottom-color: ${theme.colors.primary};
    }
    
    &:read-only {
      opacity: 0.6;
    }
  `
      : `
    border: 1px solid ${theme.colors.border};
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.background};
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}20;
    }
  `}
  
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  transition: ${({ theme }) => theme.transitions.default};
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  &:disabled {
    background: ${({ theme }) => theme.colors.surface};
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.half-width {
    max-width: 50%;
  }
`

const StyledSelect = styled.select<{ $variant?: 'default' | 'borderless' }>`
  ${({ theme, $variant }) =>
    $variant === 'borderless'
      ? `
    background-color: ${theme.colors.surfaceHover};
    border: 2px solid transparent;
    border-radius: ${theme.borderRadius.sm};
    
    &:focus {
      outline: none;
      border-bottom-color: ${theme.colors.primary};
    }
  `
      : `
    border: 1px solid ${theme.colors.border};
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.background};
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}20;
    }
  `}
  
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &.half-width {
    max-width: 50%;
  }
`

const StyledTextarea = styled.textarea<{ $variant?: 'default' | 'borderless' }>`
  ${({ theme, $variant }) =>
    $variant === 'borderless'
      ? `
    background-color: ${theme.colors.surfaceHover};
    border: none;
    border-radius: ${theme.borderRadius.sm};
    
    &:focus {
      outline: none;
      border-bottom: 2px solid ${theme.colors.primary};
    }
  `
      : `
    border: 1px solid ${theme.colors.border};
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.background};
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}20;
    }
  `}
  
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: ${({ theme }) => theme.transitions.default};
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: 'default' | 'borderless'
}

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  variant?: 'default' | 'borderless'
  children: React.ReactNode
}

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: 'default' | 'borderless'
}

export const FormInput: React.FC<FormInputProps> = ({ variant = 'default', ...props }) => {
  return <StyledInput $variant={variant} {...props} />
}

export const FormSelect: React.FC<FormSelectProps> = ({ variant = 'default', children, ...props }) => {
  return (
    <StyledSelect $variant={variant} {...props}>
      {children}
    </StyledSelect>
  )
}

export const FormTextarea: React.FC<FormTextareaProps> = ({ variant = 'default', ...props }) => {
  return <StyledTextarea $variant={variant} {...props} />
}
